import { AlertCircle, CheckCircle } from 'lucide-react'
import React, { useEffect } from 'react'
import styled from 'styled-components'
import { theme } from '../../theme/theme'

const NotificationContainer = styled.div<{ $type: 'success' | 'error' }>`
  position: fixed;
  top: 80px;
  right: 20px;
  background: ${({ theme, $type }) => ($type === 'success' ? theme.colors.primary : theme.colors.error)};
  color: white;
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.md};
  box-shadow: ${theme.shadows.lg};
  z-index: 2000;
  font-size: ${theme.typography.sm};
  max-width: 300px;
  min-width: 200px;
  
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  
  animation: slideIn 0.3s ease-out;
  
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  
  &.fade-out {
    animation: slideOut 0.3s ease-in forwards;
  }
  
  @keyframes slideOut {
    from {
      transform: translateX(0);
      opacity: 1;
    }
    to {
      transform: translateX(100%);
      opacity: 0;
    }
  }
`

const MessageText = styled.span`
  flex: 1;
  line-height: 1.4;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 2px;
  border-radius: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.8;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 1;
    background: rgba(255, 255, 255, 0.1);
  }
  
  &:focus {
    outline: 1px solid rgba(255, 255, 255, 0.3);
  }
`

export interface UploadStatus {
  type: 'success' | 'error'
  message: string
}

interface UploadStatusNotificationProps {
  status: UploadStatus | null
  onDismiss: () => void
  autoHideDelay?: number
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left'
}

export const UploadStatusNotification: React.FC<UploadStatusNotificationProps> = ({
  status,
  onDismiss,
  autoHideDelay = 5000,
  position = 'top-right',
}) => {
  // Auto-hide notification after specified delay
  useEffect(() => {
    if (status && autoHideDelay > 0) {
      const timer = setTimeout(() => {
        onDismiss()
      }, autoHideDelay)

      return () => clearTimeout(timer)
    }
  }, [status, autoHideDelay, onDismiss])

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (status && event.key === 'Escape') {
        onDismiss()
      }
    }

    if (status) {
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [status, onDismiss])

  if (!status) return null

  const IconComponent = status.type === 'success' ? CheckCircle : AlertCircle

  return (
    <NotificationContainer $type={status.type} role="alert" aria-live="polite">
      <IconComponent size={18} />
      <MessageText>{status.message}</MessageText>
      <CloseButton onClick={onDismiss} aria-label="Dismiss notification" title="Dismiss">
        ×
      </CloseButton>
    </NotificationContainer>
  )
}

// Hook for managing upload status notifications
export const useUploadStatus = () => {
  const [status, setStatus] = React.useState<UploadStatus | null>(null)

  const showSuccess = React.useCallback((message: string) => {
    setStatus({ type: 'success', message })
  }, [])

  const showError = React.useCallback((message: string) => {
    setStatus({ type: 'error', message })
  }, [])

  const clearStatus = React.useCallback(() => {
    setStatus(null)
  }, [])

  return {
    status,
    showSuccess,
    showError,
    clearStatus,
  }
}
